import format from 'date-fns/format'
import parse from 'date-fns/parse'
import { baseDate } from './dateTime'
import createType, { AbstractType } from './type'
import { z } from 'zod'

export interface DateType<NativeGeneric extends string | null = string>
  extends AbstractType<NativeGeneric> {
  readonly type: 'date'
  readonly currentValue?: NativeGeneric
  isRequired(): DateType<Exclude<NativeGeneric, null>>
  isOptional(): DateType<NativeGeneric | null>
}

export default function date(): DateType<string> {
  return {
    ...createType<DateType<string>>(),
    type: 'date',
  }
}

export function parseDate(date: string): Date {
  return parse(date, 'yyyy-MM-dd', baseDate)
}

export function stringifyDate(jsDate: Date): string {
  return format(jsDate, 'yyyy-MM-dd')
}

export function formatDate(jsDate: Date, short = false): string {
  return format(jsDate, short ? 'dd/MM/yy' : 'dd/MM/yyyy')
}

export function formatDateRelative(date: string | Date): string {
  const inputDate = new Date(date)

  if (isNaN(inputDate.getTime())) {
    throw new Error('Data inválida fornecida')
  }

  const today = new Date()

  const normalizeDate = (date: Date): Date => {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate())
  }

  const inputDateNormalized = normalizeDate(inputDate)
  const todayNormalized = normalizeDate(today)

  const diffTime = todayNormalized.getTime() - inputDateNormalized.getTime()
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

  if (diffDays === 0) return 'hoje'
  if (diffDays === 1) return 'ontem'

  if (diffDays > 1 && diffDays < 30) {
    return `${diffDays} dias`
  }

  const MONTHS_ABBREVIATED = [
    'jan',
    'fev',
    'mar',
    'abr',
    'mai',
    'jun',
    'jul',
    'ago',
    'set',
    'out',
    'nov',
    'dez',
  ] as const

  const day = inputDate.getDate()
  const month = MONTHS_ABBREVIATED[inputDate.getMonth()]
  const year = inputDate.getFullYear()

  if (year === today.getFullYear()) {
    return `${day} de ${month}`
  }

  return `${day}/${month}/${year}`
}

export const dateOrIsoString = () =>
  z
    .union([
      z.date(),
      z.string().refine(
        val => {
          return !isNaN(Date.parse(val))
        },
        {
          message: 'Data em um formato inválido',
        },
      ),
    ])
    .transform(value => {
      if (value instanceof Date) {
        return value.toISOString()
      }
      return new Date(value).toISOString()
    })
